import {Injectable} from '@angular/core';
import {BehaviorSubject, combineLatest, iif, Observable, of, of as observableOf, Subject, throwError} from 'rxjs';
import {catchError, map, switchMap, take, takeUntil} from 'rxjs/operators';
import {HttpClient, HttpErrorResponse, HttpParams} from '@angular/common/http';

import {
    AUDIT_USER_ACTION_OBJECT_ID_DELIMITER,
    AuditUserActivity,
    AuditUserActivityActionObjectIdList,
    AuditUserActivityActionObjectIdMap,
    AuditUserActivityActions,
    AuditUserActivityPagesList
} from '@app/core/model/audit-user-activity.model';
import {environment} from '@env/environment';
import {ApplicationProviderListModel} from '@app/views/admin/application-settings/models/application-provider-list.model';
import {DirectoryUser, RoleType} from '@app/core/model/user';
import {ApplicationGalleryModel} from '@app/views/admin/application-settings/models/application-gallery.model';
import {VendorInstitutionsReportItem} from '@app/core/model/institutional-model';
import {DraftIntegrationReportItem, IntegrationReportItem} from '@app/views/dashboard/dashboard.model';
import {ApplicationsService} from '@app/views/admin/provider/applications/applications.service';
import {ProjectsDashboardService} from '@app/views/dashboard/projects.service';
import {InstitutionsService} from '@app/views/admin/vendor/institutions/institutions.service';
import {DirectoryOrganization} from '@app/core/model/entity-profile';
import {AppGalleryService} from '@app/views/admin/provider/app-gallery/app-gallery.service';

@Injectable()
export class AuditUserActivityService {

    public providerApplications: ApplicationProviderListModel[] = [];
    public integrationReportItems: IntegrationReportItem[] = [];
    public draftIntegrations: DraftIntegrationReportItem[] = [];
    public allVendorsApplications: ApplicationGalleryModel[] = [];
    public vendorInstitutionsList: VendorInstitutionsReportItem[];
    public auditUserActivityPageToBeOpenedWhenReady: AuditUserActivityPagesList;
    public openAuditUserActivityPanel$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public objectIdValues: ObjectIdValues = {}; //
    public objectMaps = {
        [AuditUserActivityActionObjectIdList.ORGANIZATION_GUID]: {}, // DirectoryOrganization
        [AuditUserActivityActionObjectIdList.USER_GUID]: {}, // DirectoryUser
        [AuditUserActivityActionObjectIdList.APPLICATION_GUID]: {}, // Application
        [AuditUserActivityActionObjectIdList.INTEGRATION_ID]: {}, // Integration
        [AuditUserActivityActionObjectIdList.DNAME]: {}, // Application
        [AuditUserActivityActionObjectIdList.EMAIL]: {},
        [AuditUserActivityActionObjectIdList.USERNAME]: {},
        [AuditUserActivityActionObjectIdList.INSTITUTION_AND_APPLICATION_GUID]: {},
    };
    public districtOrgGuid: string;

    readonly SYS_ADMIN_EMAIL = 'sysadm@gg4l.com';
    readonly SYS_ADMIN_USERNAME = 'sysadm';

    private _orgGuid: string;
    private _userEmail: string;
    private _auditUserActivityActions: AuditUserActivityActions[];
    private _currentAuditUserActivityPage: AuditUserActivityPagesList;

    // if not sys admin - set orgGuid in UserAuditComponent
    // if sys admin - set orgGuid in route-related components
    public get orgGuid(): string {
        return this._orgGuid;
    }

    public set orgGuid(orgGuid: string) {
        this._orgGuid = orgGuid;
    }

    public get userEmail(): string {
        return this._userEmail;
    }

    public set userEmail(userEmail: string) {
        this._userEmail = userEmail;
    }

    public get auditUserActivityActions(): AuditUserActivityActions[] {
        return this._auditUserActivityActions;
    }

    public set auditUserActivityActions(auditUserActivityActions: AuditUserActivityActions[]) {
        this._auditUserActivityActions = auditUserActivityActions;
    }

    public get currentAuditUserActivityPage(): AuditUserActivityPagesList {
        return this._currentAuditUserActivityPage;
    }

    public set currentAuditUserActivityPage(currentAuditUserActivityPage: AuditUserActivityPagesList) {
        this._currentAuditUserActivityPage = currentAuditUserActivityPage;
    }

    static getEditAdminAuditUserActivityPage(role: RoleType): AuditUserActivityPagesList {
        return (role === RoleType.APPLICATION_ADMIN)
            ? AuditUserActivityPagesList.ADMINISTRATION_EDIT_VENDOR_ADMIN
                : (role === RoleType.INSTITUTIONAL_ADMIN)
                ? AuditUserActivityPagesList.ADMINISTRATION_EDIT_DISTRICT_ADMIN
                    : (role === RoleType.SYS_ADMIN)
                    ? AuditUserActivityPagesList.ADMINISTRATION_EDIT_SYS_ADMIN
                        : AuditUserActivityPagesList.NONE;
    }

    constructor(private http: HttpClient,
                private applicationsService: ApplicationsService,
                private projectsDashboardService: ProjectsDashboardService,
                private institutionsService: InstitutionsService,
                private appGalleryService: AppGalleryService,
    ) { }

    getAuditUserActivityData(limit: number, offset: number, filteredUserActions: AuditUserActivityActions[] = []) {
        let url = this.orgGuid
            ? `${environment.datahubBaseURI}/api/v2/audit/user/actions/organization/${this.orgGuid}?limit=${limit}&offset=${offset}`
            : `${environment.datahubBaseURI}/api/v2/audit/user/actions/organizations?limit=${limit}&offset=${offset}`;

        const userEmail =
            ((this.userEmail !== this.SYS_ADMIN_EMAIL)
                || ((filteredUserActions.length === 1)
                    && (filteredUserActions.includes(AuditUserActivityActions.AUTH_LOGIN_FAIL_BAD_CREDENTIAL))))
            ? this.userEmail
            : this.SYS_ADMIN_USERNAME;

        url = userEmail
            ? `${url}&userEmail=${userEmail}`
            : url;

        let actionNamesAndObjectIdList = [];
        if (this.currentAuditUserActivityPage) {
            actionNamesAndObjectIdList = this.getActionNameAndObjectId(filteredUserActions);

            if (actionNamesAndObjectIdList.length > 0) {
                url += `&actionNameAndObjectId=${actionNamesAndObjectIdList.join()}`;
            }
        }

        const httpOptions = {
            observe: 'response' as const,
            responseType: 'text' as const
        };

        return this.http
            .get(url, httpOptions).pipe(
                map((res: any) => {

                        let userActionsLength: number;
                        if (res.headers.has('X-Total-Count')) {
                            userActionsLength = res.headers.get('X-Total-Count');
                        }

                        const auditUserActivityData: AuditUserActivity[] = JSON.parse(res.body);

                        return {
                            userActionsLength: userActionsLength || auditUserActivityData?.length,
                            auditUserActivityData: auditUserActivityData
                        };
                    }
                ))
            .pipe(catchError(this.handleError));
    }

    createUserAction(data: AuditUserActivity): Observable<any> {
        return this.http
            .post(`${environment.datahubBaseURI}/api/v2/audit/user/actions`, data, {responseType: 'text' as const})
            .pipe(catchError(this.handleError));
    }

    private getActionNameAndObjectId(filteredUserActions: AuditUserActivityActions[]): string[] {
        const userActions: AuditUserActivityActions[] = (filteredUserActions?.length > 0)
            ? filteredUserActions
            : this.auditUserActivityActions;

        return userActions
            .map((actionName: AuditUserActivityActions) => this.getEncodedActionNamesWithEncoedDelmitersAndValue(actionName))
            .filter((actionNameAndObjectId: string) => actionNameAndObjectId);
    }

    private getEncodedActionNamesWithEncoedDelmitersAndValue(actionName: AuditUserActivityActions) {
        let objectId = this.objectIdValues[AuditUserActivityActionObjectIdMap[actionName]];
        if (objectId?.includes('|')) {
            const [institutionGuid, appGuid] = objectId.split(AUDIT_USER_ACTION_OBJECT_ID_DELIMITER);
            objectId = `${institutionGuid}${encodeURIComponent(AUDIT_USER_ACTION_OBJECT_ID_DELIMITER)}${appGuid}`;
        }
        const delimiter = encodeURIComponent('#');
        let result = `${actionName}${objectId ? delimiter + objectId : ''}`;

        if (this.currentAuditUserActivityPage === AuditUserActivityPagesList.APPLICATION_SETTINGS) {
            result = objectId ? result : '';
        }
        return result;
    }

    public getUserActionDetails(actionGuid: string): Observable<AuditUserActivity> {
        const url = `${environment.datahubBaseURI}/api/v2/audit/user/actions/${actionGuid}`;

        return this.http
            .get<AuditUserActivity>(url)
            .pipe(catchError(this.handleError));
    }

    public getDraftIntegrations(vendorOrgGuid: string): Observable<DraftIntegrationReportItem[]> {
        const url = `${environment.datahubBaseURI}/api/v2/applications/integrations/type/draft`;

        const params = new HttpParams().append('vendorOrgGuid', vendorOrgGuid || '');
        const options = {
            params
        };

        return this.http
            .get<DraftIntegrationReportItem[]>(url, options)
            .pipe(catchError(this.handleError));
    }

    public getAuditLinksRequiredData(unsubscribe$: Subject<void>, orgGuid: string, isVendor: boolean, isSysAdmin: boolean) {
        combineLatest([
            orgGuid
                ? this.applicationsService.getApplications(orgGuid).pipe(
                    take(1),
                    takeUntil(unsubscribe$)
                )
                : of([]),
            this.projectsDashboardService.getIntegrationsReport().pipe(
                take(1),
                takeUntil(unsubscribe$)
            ),
            observableOf(isSysAdmin || isVendor).pipe(
                switchMap((isVendorOrSysAdmin: boolean) =>
                    iif(
                        () => isVendorOrSysAdmin,
                        combineLatest([
                            this.getDraftIntegrations(isVendor ? orgGuid : null).pipe(
                                take(1),
                                takeUntil(unsubscribe$)
                            ),
                            this.appGalleryService.getAllVendorsApplications(),
                            isVendor
                                ? this.institutionsService.getVendorInstitutionsForAllApplications(orgGuid).pipe(
                                    take(1),
                                    takeUntil(unsubscribe$)
                                )
                                : of([])
                        ]).pipe(
                            take(1),
                            takeUntil(unsubscribe$)
                        ),
                        of([[], []])
                    )
                )
            )
        ])
            .subscribe(
                ([apps, integrationReportItems, [draftIntegrations, allVendorsApplications, vendorInstitutionsList]]:
                     [ApplicationProviderListModel[], IntegrationReportItem[], [DraftIntegrationReportItem[], ApplicationGalleryModel[], VendorInstitutionsReportItem[]]]) => {
                    this.providerApplications = apps;
                    this.integrationReportItems = integrationReportItems;
                    this.draftIntegrations = draftIntegrations;
                    this.allVendorsApplications = allVendorsApplications;
                    this.vendorInstitutionsList = vendorInstitutionsList;

                    this.openAuditUserActivityPanel();
                }, () => {
                    this.providerApplications = [];
                    this.integrationReportItems = [];
                    this.draftIntegrations = [];
                    this.allVendorsApplications = [];
                    this.vendorInstitutionsList = [];
                    this.openAuditUserActivityPanel();
                });

    }

    public addOrgsToUserAuditServiceMap(orgs: DirectoryOrganization[]): void {
        orgs.forEach((org: DirectoryOrganization) => {
            this.objectMaps[AuditUserActivityActionObjectIdList.ORGANIZATION_GUID][org.guid] = org;
        });
    }

    public addAdminsToUserAuditServiceMap(admins: DirectoryUser[]): void {
        admins.forEach((admin: DirectoryUser) => {
            this.objectMaps[AuditUserActivityActionObjectIdList.USER_GUID][admin.guid] = admin;
        });
    }

    private openAuditUserActivityPanel() {
        if ((this.auditUserActivityPageToBeOpenedWhenReady === AuditUserActivityPagesList.ADMINISTRATION_EDIT_VENDOR_ADMIN)
            || (this.auditUserActivityPageToBeOpenedWhenReady === AuditUserActivityPagesList.ADMINISTRATION_EDIT_DISTRICT_ADMIN)
            || (this.auditUserActivityPageToBeOpenedWhenReady === AuditUserActivityPagesList.ADMINISTRATION_EDIT_SYS_ADMIN)) {
            this.openAuditUserActivityPanel$.next(true);
            this.auditUserActivityPageToBeOpenedWhenReady = null;
        }
    }

    clearAuditUserActivityServiceData(): void {
        this.orgGuid = null;
        this.currentAuditUserActivityPage = null;
        this.auditUserActivityActions = null;
        this.objectIdValues = {};
        this.userEmail = '';
        this.districtOrgGuid = null;
        this.objectMaps = {
                [AuditUserActivityActionObjectIdList.ORGANIZATION_GUID]: {},
                [AuditUserActivityActionObjectIdList.USER_GUID]: {},
                [AuditUserActivityActionObjectIdList.APPLICATION_GUID]: {},
                [AuditUserActivityActionObjectIdList.INTEGRATION_ID]: {},
                [AuditUserActivityActionObjectIdList.DNAME]: {},
                [AuditUserActivityActionObjectIdList.EMAIL]: {},
                [AuditUserActivityActionObjectIdList.USERNAME]: {},
                [AuditUserActivityActionObjectIdList.INSTITUTION_AND_APPLICATION_GUID]: {},
        };
    }

    private handleError(error: HttpErrorResponse) {
        if (error.error instanceof ErrorEvent) {
            console.error('An error occurred:', error.error.message);
        } else {
            console.error(
                `Backend returned ${error.status}, ${error.error}`);
        }
        return throwError(
            'Something bad happened, please try again later.');
    }
}

interface ObjectIdValues {
    'integrationId'?: string;
    'requestGuid'?: string;
    'applicationGuid'?: string;
    'dname'?: string;
    'userGuid'?: string;
    'organizationGuid'?: string;
    'districtGuid'?: string;
    'objectId'?: number;
}




